import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, MapPin, Clock, Filter } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useAuthStore } from '@/stores/authStore';
import api from '@/lib/api';
import { Role, JobCategory, JobType, Chain } from '@hirenfire/shared';
import type { ApiResponse, JobWithAgency, PaginationMeta, JobFilters } from '@hirenfire/shared';

export function JobsListPage() {
  const user = useAuthStore((s) => s.user);
  const isAgency = user?.role === Role.AGENCY;

  const [jobs, setJobs] = useState<JobWithAgency[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<JobFilters>({
    search: '',
    category: undefined,
    type: undefined,
    chain: undefined,
    budgetMin: undefined,
    budgetMax: undefined,
    page: 1,
    limit: 12,
  });

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.category) params.set('category', filters.category);
      if (filters.type) params.set('type', filters.type);
      if (filters.chain) params.set('chain', filters.chain);
      if (filters.budgetMin) params.set('budgetMin', String(filters.budgetMin));
      if (filters.budgetMax) params.set('budgetMax', String(filters.budgetMax));
      params.set('page', String(filters.page ?? 1));
      params.set('limit', String(filters.limit ?? 12));

      const res = await api.get<ApiResponse<JobWithAgency[]>>(`/jobs?${params}`);
      if (res.data.data) setJobs(res.data.data);
      if (res.data.meta) setMeta(res.data.meta);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Jobs</h1>
          <p className="text-gray-400 mt-1">
            {isAgency ? 'Manage your job postings' : 'Find your next opportunity'}
          </p>
        </div>
        {isAgency && (
          <Link to="/jobs/create">
            <Button leftIcon={<Plus className="w-4 h-4" />}>Post a Job</Button>
          </Link>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search jobs..."
            value={filters.search ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => setShowFilters(!showFilters)}
          leftIcon={<Filter className="w-4 h-4" />}
        >
          Filters
        </Button>
      </div>

      {showFilters && (
        <Card>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select
                label="Category"
                placeholder="All Categories"
                value={filters.category ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    category: (e.target.value || undefined) as JobCategory | undefined,
                    page: 1,
                  }))
                }
                options={[
                  { value: '', label: 'All Categories' },
                  ...Object.values(JobCategory).map((c) => ({ value: c, label: c })),
                ]}
              />
              <Select
                label="Type"
                placeholder="All Types"
                value={filters.type ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    type: (e.target.value || undefined) as JobType | undefined,
                    page: 1,
                  }))
                }
                options={[
                  { value: '', label: 'All Types' },
                  ...Object.values(JobType).map((t) => ({ value: t, label: t })),
                ]}
              />
              <Select
                label="Chain"
                placeholder="All Chains"
                value={filters.chain ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    chain: (e.target.value || undefined) as Chain | undefined,
                    page: 1,
                  }))
                }
                options={[
                  { value: '', label: 'All Chains' },
                  ...Object.values(Chain).map((c) => ({ value: c, label: c })),
                ]}
              />
              <div className="flex gap-2">
                <Input
                  label="Min Budget"
                  type="number"
                  placeholder="0"
                  value={filters.budgetMin ?? ''}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      budgetMin: e.target.value ? Number(e.target.value) : undefined,
                      page: 1,
                    }))
                  }
                />
                <Input
                  label="Max Budget"
                  type="number"
                  placeholder="Any"
                  value={filters.budgetMax ?? ''}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      budgetMax: e.target.value ? Number(e.target.value) : undefined,
                      page: 1,
                    }))
                  }
                />
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : jobs.length === 0 ? (
        <Card>
          <CardBody className="py-16 text-center">
            <p className="text-gray-500 text-lg">No jobs found</p>
            <p className="text-gray-600 text-sm mt-2">Try adjusting your filters</p>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job) => (
              <Link key={job.id} to={`/jobs/${job.id}`}>
                <Card className="h-full hover:border-dark-500 transition-all duration-200">
                  <CardBody className="flex flex-col h-full">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate">{job.title}</h3>
                        <p className="text-sm text-gray-400 mt-0.5">{job.agency.displayName}</p>
                      </div>
                    </div>

                    <p className="text-sm text-gray-400 line-clamp-2 mb-4 flex-1">
                      {job.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge variant="primary">{job.type}</Badge>
                      <Badge variant="purple">{job.category}</Badge>
                      <Badge>{job.chain}</Badge>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-dark-600">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {job.timezone && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {job.timezone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(job.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-success-400">
                        ${job.budget} {job.currency}
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </Link>
            ))}
          </div>

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="secondary"
                size="sm"
                disabled={filters.page === 1}
                onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-400 px-4">
                Page {meta.page} of {meta.totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
